import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { BUSINESS } from "@/data/services";

const LINKS = [
  { label: "Services", id: "services", testid: "nav-link-services" },
  { label: "Estimator", id: "estimator", testid: "nav-link-estimator" },
  { label: "Standard", id: "manifesto", testid: "nav-link-manifesto" },
  { label: "Gallery", id: "gallery", testid: "nav-link-gallery" },
  { label: "Areas", id: "service-area", testid: "nav-link-areas" },
  { label: "Book", id: "booking", testid: "nav-link-book" },
];

export const Logo = ({ compact = false }) => (
  <div className="flex items-center gap-3" data-testid="nav-logo">
    <img
      src="/logo.png"
      alt="Elite Rims & Trims logo"
      className="w-11 h-11 rounded-full ring-1 ring-edge object-cover"
    />
    {!compact && (
      <div className="leading-none">
        <div className="font-syne font-extrabold text-sm tracking-widest uppercase text-slate-100">
          Elite Rims
        </div>
        <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-volt">&amp; Trims</div>
      </div>
    )}
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-carbon/85 backdrop-blur-xl border-b border-edge" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <button onClick={() => window.__lenis?.scrollTo(0, { duration: 1.2 })} aria-label="Back to top">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="font-mono2 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-volt transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="nav-call-btn"
            href={`tel:${BUSINESS.phone}`}
            className="flex items-center gap-2 font-mono2 text-xs tracking-widest text-slate-300 hover:text-volt border border-edge hover:border-volt/60 rounded-full px-4 py-2 transition-colors duration-300"
          >
            <Phone size={13} /> {BUSINESS.phone}
          </a>
          <button
            data-testid="nav-book-btn"
            onClick={() => go("booking")}
            className="bg-volt text-carbon font-syne font-bold text-xs uppercase tracking-widest rounded-full px-5 py-2.5 hover:bg-white hover:text-carbon transition-colors duration-300"
          >
            Book a Service
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-btn"
          className="md:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-carbon/95 backdrop-blur-xl border-b border-edge px-6 py-6 flex flex-col gap-5"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="text-left font-syne font-bold text-lg uppercase tracking-wider text-slate-200 hover:text-volt transition-colors"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-call-btn-mobile"
              href={`tel:${BUSINESS.phone}`}
              className="flex items-center gap-2 font-mono2 text-sm text-volt"
            >
              <Phone size={15} /> {BUSINESS.phone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
